/**
 * Mini-Project Roles API client.
 *
 * DEV-1088 / Roles Phase 1. Lightweight role profiles + invitation
 * lifecycle. Two backend tables:
 *   • mini_project_roles            — slot definitions
 *   • mini_project_role_assignments — invitation + active fill rows
 *     (5-status state machine: pending_invitation → active | declined |
 *     revoked → removed)
 *
 * Roles are descriptive — they don't grant platform permissions. See
 * POLICY_TEAM_ROSTERS.md (pending amendment) for the three-layer team
 * model (tier / display title / assigned roles).
 *
 * All calls go through apiFetch which handles base URL, Bearer JWT, and
 * 401-driven re-auth (see ./client.ts).
 */
import { apiFetch } from "./client";

// ── Types ───────────────────────────────────────────────────────

/** Project-defined role slot. */
export interface MiniProjectRole {
  roleGuid: string;
  miniProjectGuid: string;
  /** Set iff the row was seeded from a Playbook on instantiation.
   *  NULL for Lead-added custom roles. Traceability only — Lead can
   *  edit/delete regardless. */
  playbookRoleKey?: string | null;
  name: string;
  description?: string | null;
  /** Multi-line free text; render as bullet list by splitting on `\n`. */
  responsibilities?: string | null;
  /** Multi-line free text; same parse-by-newlines convention. */
  skillsDesired?: string | null;
  /** NULL = "just need someone". Positive integer = wanted headcount. */
  headcountNeeded?: number | null;
  position: number;
  /** Active assignments — users who accepted and are currently filling. */
  activeAssignees: MiniProjectRoleAssignee[];
  /** Pending invitations — users invited, not yet responded. Lead-only
   *  surface on the client; the API returns them on every read. */
  pendingInvitations: MiniProjectRoleAssignee[];
  createdAt: string;
  updatedAt: string;
}

/** Invitation or active fill row, summarized for the Roles list. */
export interface MiniProjectRoleAssignee {
  assignmentGuid: string;
  userGuid: string;
  displayName: string;
  /** 'pending_invitation' | 'active' | 'declined' | 'revoked' | 'removed'. */
  status: string;
  invitedAt: string;
  invitedByUserGuid: string;
  invitationRespondedAt?: string | null;
  activatedAt?: string | null;
  endedAt?: string | null;
}

/** Payload for POST /roles. Position is optional; server computes
 *  next-available if omitted. */
export interface CreateMiniProjectRoleRequest {
  name: string;
  description?: string | null;
  responsibilities?: string | null;
  skillsDesired?: string | null;
  headcountNeeded?: number | null;
  position?: number | null;
}

/** Patch shape for PUT /roles/{roleGuid}. Only present fields are
 *  mutated. Name cannot be cleared (server rejects empty). */
export interface UpdateMiniProjectRoleRequest {
  name?: string;
  description?: string | null;
  responsibilities?: string | null;
  skillsDesired?: string | null;
  headcountNeeded?: number | null;
  position?: number | null;
}

/** Body for POST /roles/{roleGuid}/invitations. */
export interface InviteToMiniProjectRoleRequest {
  userGuid: string;
  /** Optional Lead message shown in the bell notification and
   *  Action Center row. Max 2000 chars (server-enforced). */
  message?: string | null;
}

/** Body for POST /mini-project-role-invitations/{assignmentGuid}/respond. */
export interface RespondToMiniProjectRoleInvitationRequest {
  /** 'accept' or 'decline'. */
  action: "accept" | "decline";
  /** Optional personal note from the invitee. */
  note?: string | null;
}

/** Response from the invitee-side respond endpoint. */
export interface RespondToMiniProjectRoleInvitationResponse {
  message: string;
  /** Post-transition status. 'active' on accept, 'declined' on decline. */
  status: string;
}

// ── Fetch functions ─────────────────────────────────────────────
//
// Lead-side endpoints sit under /api/mini-projects/{guid}/roles/...
// Invitee-side respond lives under /api/mini-project-role-invitations/
// because the invitee doesn't know (or shouldn't have to know) which
// project a given invitation belongs to.

/** List roles for a project, with active assignees + pending invitations
 *  inline. Read auth = CanReadMiniProject. */
export const fetchMiniProjectRoles = (miniProjectGuid: string) =>
  apiFetch<MiniProjectRole[]>(`/api/mini-projects/${miniProjectGuid}/roles`);

/** Create a new role slot. Lead-only. */
export const createMiniProjectRole = (
  miniProjectGuid: string,
  req: CreateMiniProjectRoleRequest,
) =>
  apiFetch<MiniProjectRole>(`/api/mini-projects/${miniProjectGuid}/roles`, {
    method: "POST",
    body: JSON.stringify(req),
  });

/** Update a role slot. Patch semantics — only present fields are
 *  mutated. Lead-only. */
export const updateMiniProjectRole = (
  miniProjectGuid: string,
  roleGuid: string,
  req: UpdateMiniProjectRoleRequest,
) =>
  apiFetch<MiniProjectRole>(`/api/mini-projects/${miniProjectGuid}/roles/${roleGuid}`, {
    method: "PUT",
    body: JSON.stringify(req),
  });

/** Delete a role slot. Cascades to all assignments (pending + active).
 *  Team members stay on the team with display_title intact. Lead-only. */
export const deleteMiniProjectRole = (miniProjectGuid: string, roleGuid: string) =>
  apiFetch<void>(`/api/mini-projects/${miniProjectGuid}/roles/${roleGuid}`, {
    method: "DELETE",
  });

/** Lead invites a user to fill a role. Creates a pending_invitation
 *  assignment, bell notification, and Action Center WorkflowRequest in
 *  one transaction. Returns the created assignment summary. */
export const inviteToMiniProjectRole = (
  miniProjectGuid: string,
  roleGuid: string,
  req: InviteToMiniProjectRoleRequest,
) =>
  apiFetch<MiniProjectRoleAssignee>(
    `/api/mini-projects/${miniProjectGuid}/roles/${roleGuid}/invitations`,
    {
      method: "POST",
      body: JSON.stringify(req),
    },
  );

/** Lead revokes a pending invitation OR removes an active fill.
 *  Status transitions: pending_invitation → revoked, active → removed.
 *  Cancels the linked WorkflowRequest and dismisses the invitee's bell
 *  notification (if still un-dismissed). */
export const endMiniProjectRoleAssignment = (
  miniProjectGuid: string,
  roleGuid: string,
  assignmentGuid: string,
  reason?: string,
) => {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return apiFetch<void>(
    `/api/mini-projects/${miniProjectGuid}/roles/${roleGuid}/assignments/${assignmentGuid}${qs}`,
    { method: "DELETE" },
  );
};

/** Invitee accepts or declines a role invitation. On accept the user is
 *  auto-added to mini_project_team_members as 'member' if not already
 *  on the team (Lead untouched). On decline the assignment moves to
 *  terminal 'declined'. */
export const respondToMiniProjectRoleInvitation = (
  assignmentGuid: string,
  req: RespondToMiniProjectRoleInvitationRequest,
) =>
  apiFetch<RespondToMiniProjectRoleInvitationResponse>(
    `/api/mini-project-role-invitations/${assignmentGuid}/respond`,
    {
      method: "POST",
      body: JSON.stringify(req),
    },
  );

/** DEV-1091. Self-resign from an active Mini-Project role assignment.
 *  Only the assignee themselves can call this. The endpoint transitions
 *  status 'active' → 'resigned' (terminal), captures the optional
 *  reason on the assignment row, and emits a mini_project.role_resignation
 *  acknowledgment WorkflowRequest to the original inviter. The team
 *  roster is NOT touched (per POLICY_TEAM_ROSTERS §3.9 — cascade is
 *  tracked under DEV-1090 separately). */
export interface ResignFromMiniProjectRoleRequest {
  /** Optional reason for resigning. Surfaces in the Lead's ack. */
  reason?: string | null;
}

export interface ResignFromMiniProjectRoleResponse {
  message: string;
  status: string;
}

export const resignFromMiniProjectRole = (
  assignmentGuid: string,
  req: ResignFromMiniProjectRoleRequest,
) =>
  apiFetch<ResignFromMiniProjectRoleResponse>(
    `/api/mini-project-role-invitations/${assignmentGuid}/resign`,
    {
      method: "POST",
      body: JSON.stringify(req),
    },
  );
